import { useEffect, useMemo } from 'react';
import { getApiErrorDetail, useFeatureFlags, useIsFeatureFlagActive } from '@klareospace/pharos-lib-front-utils';
import type { ApiErrorDetail, ImportDetail } from '@klareospace/pharos-lib-types';
import { useNavigate, useParams } from '@tanstack/react-router';

import { FEATURE_FLAGS } from '&/feature-flags';

import { flattenImportLineErrors } from '../utils';
import type { RejectedRow } from '../utils';
import { useImport } from './use-import';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseImportReportPageReturn {
  error: ApiErrorDetail | null;
  hiddenErrors: number;
  importDoc: ImportDetail | undefined;
  isLoading: boolean;
  isReady: boolean;
  rows: RejectedRow[];
  totalErrors: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Composes the import report page's guard + data wiring — mirrors `useImportHistoryPage`. */
export function useImportReportPage(): UseImportReportPageReturn {
  const navigate = useNavigate();
  const { isLoading: isFeatureFlagsLoading } = useFeatureFlags();
  const isFileImportActive = useIsFeatureFlagActive(FEATURE_FLAGS.FILE_IMPORT);
  const isReady = !isFeatureFlagsLoading && isFileImportActive;
  const { importId } = useParams({ from: '/_authenticated/imports/history_/$importId' });
  const { data: importDoc, error, isLoading } = useImport(importId, { enabled: isReady });

  useEffect(() => {
    if (isFeatureFlagsLoading || isFileImportActive) return;
    void navigate({ replace: true, to: '/orders' });
  }, [isFeatureFlagsLoading, isFileImportActive, navigate]);

  const rows = useMemo<RejectedRow[]>(() => (importDoc ? flattenImportLineErrors(importDoc) : []), [importDoc]);
  const totalErrors = importDoc?.totalLineErrors ?? 0;
  const hiddenErrors = Math.max(0, totalErrors - rows.length);

  return {
    error: error ? getApiErrorDetail(error) : null,
    hiddenErrors,
    importDoc,
    isLoading,
    isReady,
    rows,
    totalErrors,
  };
}
